import clsx from "clsx";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/Interface/Button/Button";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { PasswordField } from "@/components/Interface/FormFields/PasswordField/PasswordField";
import { Link } from "@/components/Interface/Link/Link";
import { TwoFactorCodeModal } from "@/components/Portal/UserAccount/Security/TwoFactorCodeModal";
import { VerifyEmailModal } from "@/components/Welcome/VerifyEmailModal";
import { useAlert } from "@/hooks/useAlert";
import { useCustomerLogin } from "@/hooks/useCustomerLogin";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { FCC } from "@/types";
import { FORGOT_PASSWORD_URL } from "@/utils/apiUrls";
import {
  createLoginErrorHandler,
  LOGIN_ERROR_MESSAGES,
  LoginFormFields,
} from "@/utils/loginErrorHandler";

import { MagicLinkEmailSent } from "./MagicLinkEmailSent";
import { MagicLinkRequestForm } from "./MagicLinkRequestForm";
import { WelcomeCard } from "./WelcomeCard";

type LoginView = "password" | "magic-link-request" | "magic-link-sent";

interface Props {
  joinTribeOnLogin?: () => void;
  /** Magic link login is only enabled on the main welcome screen. */
  enableMagicLinkLogin?: boolean;
}

export const LogInForm: FCC<Props> = ({
  children,
  joinTribeOnLogin,
  enableMagicLinkLogin = false,
}) => {
  const [loginView, setLoginView] = useState<LoginView>("password");
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting },
    setError,
    watch,
  } = useForm<LoginFormFields>({
    defaultValues: {
      emailAddress: "",
      password: "",
    },
  });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showAlert = useAlert();
  const [forceEnableButton, setForceEnableButton] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const {
    isLoading,
    shouldShowTwoFactorModal,
    shouldShowVerifyEmailModal,
    twoFactorMethod,
    twoFactorGrantToken,
    loginAsCustomer,
    setIsLoading,
    setShouldShowTwoFactorModal,
    setShouldShowVerifyEmailModal,
  } = useCustomerLogin({
    onSuccess: () => {
      // If VerifyEmailModal is shown we let it handle the onSuccess case
      if (shouldShowVerifyEmailModal) {
        return;
      }

      // If TwoFactorModal is shown we let it handle the onSuccess case
      if (shouldShowTwoFactorModal) {
        return;
      }

      handleAuthSuccess();
    },
    onError: (error) => {
      // Create error handler with current 2FA modal state
      const errorHandler = createLoginErrorHandler(
        setError,
        t,
        showAlert,
        "emailAddress",
      );
      errorHandler(error);
    },
  });

  useEffect(() => {
    // If button is disabled, set a 10-second timer to force reenable it
    if (isLoading || isSubmitting) {
      setForceEnableButton(false);
      const timer = setTimeout(() => {
        setForceEnableButton(true);
      }, 10000);

      return () => clearTimeout(timer);
    } else {
      // Reset force enable when button is naturally enabled
      setForceEnableButton(false);
    }
  }, [isLoading, isSubmitting]);

  const onSubmit = async (data: LoginFormFields) => {
    setSubmitting(true);

    try {
      await loginAsCustomer({
        email: data.emailAddress,
        password: data.password,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const email = watch("emailAddress");
  const password = watch("password");

  const handleAuthSuccess = () => {
    if (joinTribeOnLogin) {
      setTimeout(() => {
        joinTribeOnLogin();
        navigate(portalRoutes.friendsWithBenefits.groupOverview);
      }, 3000);
    } else {
      navigate(portalRoutes.cockpit.base);
    }
  };

  const resetToPasswordLogin = () => setLoginView("password");

  if (enableMagicLinkLogin && loginView === "magic-link-request") {
    return (
      <MagicLinkRequestForm
        onBack={resetToPasswordLogin}
        onSuccess={() => setLoginView("magic-link-sent")}
      />
    );
  }

  if (enableMagicLinkLogin && loginView === "magic-link-sent") {
    return <MagicLinkEmailSent onBack={resetToPasswordLogin} />;
  }

  return (
    <>
      <WelcomeCard>
        <form
          className="flex w-full flex-col gap-6"
          onSubmit={handleSubmit(onSubmit)}
        >
          <h1 className="text-headline-md font-semibold leading-sm text-foreground-primary">
            {t("translation:newWelcome.logInForm.title")}
          </h1>

          <div className="flex flex-col gap-6">
            <FormField<LoginFormFields>
              id="emailAddress"
              name="emailAddress"
              label={t("translation:Common.generalForm.fields.email.label")}
              type="email"
              register={register}
              errors={errors}
              options={{
                required: t(
                  "translation:Common.generalForm.validation.emailRequired",
                ),
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: t(
                    "translation:Common.generalForm.validation.emailNotValid",
                  ),
                },
              }}
            />

            <div className="flex flex-col gap-1">
              <PasswordField<LoginFormFields>
                id="password"
                name="password"
                label={t(
                  "translation:Common.generalForm.fields.password.label",
                )}
                register={register}
                errors={errors}
                options={{
                  required: t(
                    "translation:Common.generalForm.validation.passwordRequired",
                  ),
                }}
              />
              <a
                className={clsx(
                  // Hover/rest state uses border-b thickening on hover
                  // instead of a plain text-decoration underline. -mb-px cancels
                  // the +1px border so content below does not shift.
                  "mt-1 w-fit border-b border-solid border-foreground-primary text-body-xs font-semibold text-foreground-primary hover:-mb-px hover:border-b-2",
                  // Bang-prefixed: a global legacy stylesheet resets `a:focus`
                  // (outline/box-shadow) with plain (non-!important)
                  // declarations that would otherwise win the cascade over
                  // same-specificity Tailwind utilities.
                  "focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-4 focus-visible:!outline-foreground-primary",
                )}
                href={FORGOT_PASSWORD_URL.toString()}
                aria-label="forgot password link"
                target="_self"
                rel="noreferrer"
              >
                {t("translation:newWelcome.logInForm.forgotPassword")}
              </a>
            </div>
          </div>

          {shouldShowVerifyEmailModal && (
            <VerifyEmailModal
              email={email}
              password={password}
              handleSuccess={() => {
                setShouldShowVerifyEmailModal(false);

                // Trigger the navigation that was being skipped by the onSuccess early return
                handleAuthSuccess();
              }}
              handleClose={() => {
                setShouldShowVerifyEmailModal(false);
                setSubmitting(false);
                setIsLoading(false);

                reset({
                  emailAddress: "",
                  password: "",
                });
              }}
            />
          )}

          {shouldShowTwoFactorModal && (
            <TwoFactorCodeModal
              variant="dark"
              userName={email}
              userPassword={password}
              setShowModal={setShouldShowTwoFactorModal}
              method={twoFactorMethod}
              successAction={() => handleAuthSuccess()}
              type="login"
              twoFactorGrantToken={twoFactorGrantToken}
              onAccountLocked={() => {
                const accountLockedMessage = t(
                  LOGIN_ERROR_MESSAGES.ACCOUNT_LOCKED,
                );

                setError("emailAddress", { message: accountLockedMessage });
                setError("password", { message: accountLockedMessage });
              }}
            />
          )}

          <div className="flex flex-col items-center gap-4">
            <Button
              variant="primary"
              type="submit"
              disabled={!forceEnableButton && (isLoading || submitting)}
            >
              {t("translation:newWelcome.logInForm.submitButton")}
            </Button>

            {enableMagicLinkLogin && (
              <Link
                type="button"
                onClick={() => setLoginView("magic-link-request")}
                data-testid="magic-link-login-trigger"
              >
                {t("translation:newWelcome.magicLink.linkOnLogin")}
              </Link>
            )}
          </div>
        </form>
      </WelcomeCard>

      <div className="flex justify-center py-3">{children}</div>
    </>
  );
};
